import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Grid } from '@material-ui/core';
import { GlobalHotKeys } from 'react-hotkeys';

import TopBar from 'components/TopBar';
import LeftPanel from 'components/LeftPanel';
import RightPanel from 'components/RightPanel';
import ConversationPanel from 'components/ConversationPanel';

import {
  apiFetchAiPrompts,
  apiFetchConversation,
  apiFetchLead,
  apiFetchMessageTemplates,
  apiMarkLeadAsDone,
} from 'state/lead.state';
import { apiFetchScriptResponse } from 'state/script.state';
import { RootState } from 'state/store';

import pusherService from 'services/pusher.service';
import { KeyMap } from 'utils/constants';
import { goToHomePage } from 'utils/functions';

import { ObjectLiteral, LeadIcon } from 'typings';

const defaultDistributionAudio = require('assets/sounds/dingNotification.mp3');
const callDistributionAudio = require('assets/sounds/callNotification.mp3');

const Layout = styled('div')`
  position: relative;
  height: 100vh;
  background-color: ${(props) => props.theme.palette.background.paper};
  padding: ${(props) => props.theme.spacing(1, 3)};
  overflow: hidden;

  a {
    color: ${(props) => props.theme.palette.secondary.main};
    text-decoration: none;
  }

  .grid-wrapper {
    margin-top: ${(props) => props.theme.spacing(1)};

    .grid-container {
      padding: ${(props) => props.theme.spacing(1, 0)};
      height: 100%;

      .MuiGrid-item {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }

  .transition-screen {
    margin: ${(props) => props.theme.spacing(0, -3)};
  }

  .grid-panel {
    height: calc(100vh - ${(props) => props.theme.spacing(8)});
    overflow-y: scroll;
  }
`;

const ControlGrid = () => {
  return (
    <div className="grid-wrapper">
      <Grid className="grid-container" container spacing={3}>
        <Grid item xs={12} sm={12} md={3}>
          <LeftPanel />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <ConversationPanel />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <RightPanel />
        </Grid>
      </Grid>
    </div>
  );
};

const ControlCenter = () => {
  const { leadId: routeParamLeadId } = useParams<ObjectLiteral>();
  const dispatch = useDispatch();
  const { leadId, agentId, isMarkedDone, isDoneActionAutomatic } = useSelector(
    (state: RootState) => ({
      leadId: state.lead.entity?.id,
      agentId: state.lead.entity?.agentId,
      isMarkedDone: state.lead.isMarkedDone,
      isDoneActionAutomatic: state.lead.isDoneActionAutomatic,
    }),
    shallowEqual
  );
  const { isOnCall, assignedLeadId, isNewDistribution, leadIcon } = useSelector(
    (state: RootState) => ({
      isOnCall: state.concierge.isOnCall,
      assignedLeadId: state.concierge.assignedLeadId,
      isNewDistribution: state.concierge.isNewDistribution,
      leadIcon: state.concierge.assignedLeadIcon,
    }),
    shallowEqual
  );
  const soundEnabled = useSelector((state: RootState) => state.app.soundEnabled);
  const distributionVolume = useSelector((state: RootState) => state.app.distributionVolume);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const shouldAutoDone = !isOnCall && isDoneActionAutomatic && leadId === assignedLeadId;

  useEffect(() => {
    // fetch new lead when leadId in the browser url changes
    if (routeParamLeadId) {
      dispatch(apiFetchLead(routeParamLeadId));
    }
  }, [dispatch, routeParamLeadId]);

  useEffect(() => {
    // subscribe to lead channel when a new lead is fetched from the api
    if (leadId) {
      pusherService.subscribeToLeadChannel(leadId);

      if (agentId) {
        pusherService.subscribeToCustomerChannel(agentId);
      }

      dispatch(apiFetchConversation(leadId));
      dispatch(apiFetchAiPrompts(leadId));
      dispatch(apiFetchMessageTemplates(leadId));
      dispatch(apiFetchScriptResponse(leadId));
    }
  }, [dispatch, leadId, agentId]);

  // only auto done if not on a call
  useEffect(() => {
    if (shouldAutoDone) {
      dispatch(apiMarkLeadAsDone());
    }
  }, [dispatch, shouldAutoDone]);

  useEffect(() => {
    if (isMarkedDone) {
      pusherService.unsubscribeFromLeadChannel();
      pusherService.unsubscribeFromCustomerChannel();
      goToHomePage();
    }
  }, [isMarkedDone]);

  useEffect(() => {
    const audioSrc = leadIcon === LeadIcon.CALL ? callDistributionAudio : defaultDistributionAudio;
    audioRef.current = new Audio(audioSrc);

    // only adjust volume of new distributions other than calls
    if (leadIcon !== LeadIcon.CALL) {
      audioRef.current.volume = distributionVolume;
    }
  }, [distributionVolume, leadIcon]);

  useEffect(() => {
    if (isNewDistribution && soundEnabled && audioRef?.current) {
      audioRef.current.play();
    }
  }, [isNewDistribution, soundEnabled]);

  if (!leadId) {
    return null;
  }

  return (
    <GlobalHotKeys keyMap={KeyMap}>
      <Layout>
        <TopBar />
        <ControlGrid />
      </Layout>
    </GlobalHotKeys>
  );
};

export default ControlCenter;
